import withStyles from '@material-ui/core/styles/withStyles';
import moment from 'moment';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import SchedulerDayPopover from 'Components/Modals/ClientSchedulerModal/Default/MonthlyCalendar/SchedulerDayPopover';
import usePopoverState from 'Hooks/usePopoverState';
import {
  useClientSchedulerDispatchContext,
  fetchDataFromLinkQueryParam,
  setNewLinkQueryParam,
  useClientSchedulerStateContext,
} from 'Context/NewClientSchedulerContext';
import fr from 'facet-rest';
import SchedulerDayButton from './SchedulerDayButton';

const styles = theme => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 'calc(100% /7)',
    borderRight: '1px solid',
    borderRightColor: theme.palette.grey[200],
    borderBottom: '1px solid',
    borderBottomColor: theme.palette.grey[200],
  },
  dateContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
  },
  dateButton: {
    minWidth: 32,
    height: 32,
    borderRadius: '50%',
    color: theme.palette.common.white,
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
  },
  availableColor: {
    backgroundColor: theme.palette.facet.accentGreen2,
  },
  someAvailabilityColor: {
    backgroundColor: theme.palette.facet.accentBlue11,
  },
  noAvailabilityColor: {
    backgroundColor: theme.palette.facet.accentGray,
    color: theme.palette.facet.mediumGray,
  },
  notInCurrentMonth: {
    display: 'none',
  },
  weekendDay: {
    color: theme.palette.facet.accentGray,
    fontWeight: 'bold',
  },
  dateItemText: {
    fontSize: theme.typography.body1.fontSize,
    fontWeight: 'bold',
    color: theme.palette.facet.primaryGray,
    margin: theme.spacing(0, 5),
    textAlign: 'center',
  },
  unavailableDateItemText: {
    color: theme.palette.facet.mediumGray,
  },
  projectedWeekColor: {
    backgroundColor: '#64E2BB2B',
  },
});

const getCalendarDayTextFromCapacity = (capacity) => {
  if (capacity > 2) {
    return 'More than two available times';
  } if (capacity === 1 || capacity === 2) {
    return 'One or two available times';
  }
  return 'No Availability';
};

const DesktopSchedulerDay = ({
  classes,
  day,
  date,
  onDayNumberClick,
  currentMonth,
  items,
}) => {
  const { currentCollection } = useClientSchedulerStateContext();
  const dispatch = useClientSchedulerDispatchContext();
  const {
    open,
    anchorEl,
    openPopover,
    handleClose,
    handleEnter,
  } = usePopoverState({});

  const availabilityPeriodResource = currentCollection.resources().head();
  const inCurrentMonth = currentMonth.isSame(date, 'month');
  const [availabilityDayItem] = items;

  if (!availabilityDayItem) {
    return (
      <div
        className={classNames(classes.root)}
      >
        <div className={classNames(classes.dateContainer)} />
      </div>
    );
  }

  const { resource } = availabilityDayItem;
  const capacity = parseInt(resource.attr('capacity'), 10);
  const meetingAvailabilities = resource.attr('meetingAvailabilities').resources().list();
  const calendarDayText = getCalendarDayTextFromCapacity(capacity);

  const handleClickAvailableTime = (meetingAvailabilityResource) => {
    setNewLinkQueryParam(dispatch, meetingAvailabilityResource.relationship(fr.relationships.scheduleCalendarEvent.name).link('related').url());
    dispatch({ type: 'setPrevLink', prevLink: availabilityPeriodResource.link('self').url() });
    dispatch({
      type: 'setConfirmTime',
      selectedMeetingStart: meetingAvailabilityResource.attr('startTime'),
      selectedMeetingEnd: meetingAvailabilityResource.attr('endTime'),
      bufferDuration: meetingAvailabilityResource.attr('bufferLength'),
    });
    fetchDataFromLinkQueryParam(dispatch);
  };

  const handleDayNumberClick = (event) => {
    onDayNumberClick(date);
    openPopover(event);
  };

  // We should show all days when the EveningWeekends feature is enable, so let's force the SchedulerDayButton component to display the same as the weekdays
  const showAllDays = true;
  return (
    <div
      className={classNames(classes.root)}
    >
      <div
        className={classNames(classes.dateContainer)}
        test="client-desktop-scheduler-month-day"
      >
        <SchedulerDayButton
          capacity={capacity}
          day={day}
          handleDayNumberClick={handleDayNumberClick}
          inCurrentMonth={inCurrentMonth}
          test="client-desktop-scheduler-day-number"
          isWeekday={showAllDays}
        />
        <div className={classNames(classes.dateItemText, {
          [classes.unavailableDateItemText]: capacity === 0,
        })}
        >
          {calendarDayText}
        </div>
      </div>
      <SchedulerDayPopover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        onEnter={handleEnter}
        onClickAvailableTime={handleClickAvailableTime}
        meetingAvailabilities={meetingAvailabilities}
      />
    </div>
  );
};

DesktopSchedulerDay.defaultProps = {
  day: null,
  date: null,
  onDayNumberClick: null,
  items: [],
};

DesktopSchedulerDay.propTypes = {
  classes: PropTypes.object.isRequired,
  day: PropTypes.number,
  date: PropTypes.instanceOf(moment),
  onDayNumberClick: PropTypes.func,
  currentMonth: PropTypes.object.isRequired,
  items: PropTypes.array,
};

export default withStyles(styles)(DesktopSchedulerDay);
